import { ISkiaRect } from '@core/entities/skia/SkiaRect/ISkiaRect'
import { plainToInstance } from 'class-transformer'
import { Point } from '@core/entities/skia/types'
import { SkiaShapeBase } from '@core/entities/skia/SkiaShapeBase/SkiaShapeBase'
import { SiteBoxDocumentAnnotation } from '@core/entities/SiteBoxDocumentAnnotation'
import { GroupMetadata } from '@core/entities/skia/types'
export class SkiaPolygon extends SkiaShapeBase<ISkiaRect> {
  type: 'POLYGON'
  points: Point[]

  public static new(payload: unknown): SkiaPolygon {
    return plainToInstance(SkiaPolygon, payload)
  }

  public static newFromSiteboxDocumentAnnotation(
    payload: SiteBoxDocumentAnnotation,
    metadata?: GroupMetadata,
  ): SkiaPolygon {
    let transformToSkiaPolygon = {
      id: payload.uuid,
      type: 'POLYGON',
      points: payload.annotationData.points.map((point) => ({
        x: point[0],
        y: point[1],
      })),
    }

    if (metadata) {
      transformToSkiaPolygon = {
        ...transformToSkiaPolygon,
        ...metadata,
      }
    }

    return plainToInstance(SkiaPolygon, transformToSkiaPolygon)
  }

  public static newFromSiteboxDocumentAnnotationImage(
    payload: SiteBoxDocumentAnnotation,
    {
      widthPixelsPerPoint,
      heightPixelsPerPoint,
    }: {
      widthPixelsPerPoint: number
      heightPixelsPerPoint: number
    },
    metadata?: GroupMetadata,
  ): SkiaPolygon {
    let transformToSkiaPolygon = {
      id: payload.uuid,
      type: 'POLYGON',
      points: payload.annotationData.points.map((point) => ({
        x: point[0] * widthPixelsPerPoint,
        y: point[1] * heightPixelsPerPoint,
      })),
    }

    if (metadata) {
      transformToSkiaPolygon = {
        ...transformToSkiaPolygon,
        ...metadata,
      }
    }

    return plainToInstance(SkiaPolygon, transformToSkiaPolygon)
  }

  isInBounds(point: Point): boolean {
    let isInside = false
    for (
      let i = 0, j = this.points.length - 1;
      i < this.points.length;
      j = i++
    ) {
      const xi = this.points[i].x,
        yi = this.points[i].y
      const xj = this.points[j].x,
        yj = this.points[j].y

      const intersect =
        yi > point.y !== yj > point.y &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
      if (intersect) isInside = !isInside
    }
    return isInside
  }

  dragCorners(): Point[] {
    return this.points
  }

  resizeOnDrag({
    cornerIndex,
    x,
    y,
  }: {
    cornerIndex: number
    x: number
    y: number
  }) {
    this.points = this.points.map((point, index) => {
      if (index === cornerIndex) {
        return { x, y }
      }
      return point
    })
  }

  move({ x, y }: Point) {
    const currentTopLeft = this.labelAnchor()
    const deltaX = x - currentTopLeft.x
    const deltaY = y - currentTopLeft.y

    this.points = this.points.map((point) => ({
      x: point.x + deltaX,
      y: point.y + deltaY,
    }))
  }

  moveByDelta(currentPoint: Point, previousPoint: Point) {
    this.points = this.points.map((point) => ({
      x: point.x + (currentPoint.x - previousPoint.x),
      y: point.y + (currentPoint.y - previousPoint.y),
    }))
  }

  labelAnchor(): Point {
    return this.points.reduce(
      (acc, point) => {
        if (point.x < acc.x) acc.x = point.x
        if (point.y < acc.y) acc.y = point.y
        return acc
      },
      { x: Infinity, y: Infinity },
    )
  }

  updateFromPoints(_: Point | null, endPoint: Point): void {
    // For polygons, we only want to update the last point position while dragging
    if (this.points.length === 0) {
      this.points = [endPoint]
    } else {
      // Update the last point position
      const lastIndex = this.points.length - 1
      this.points[lastIndex] = endPoint
    }
  }

  shapeCardAnchor(): Point {
    return this.points.reduce(
      (acc, point) => {
        if (point.x < acc.x) acc.x = point.x // Keep the smallest x (leftmost)
        if (point.y > acc.y) acc.y = point.y // Now keep the largest y (bottommost)
        return acc
      },
      { x: Infinity, y: -Infinity }, // Start with -Infinity for y to ensure we get the highest value
    )
  }

  addPoint(point: Point): void {
    // Add as a new point
    this.points.push(point)
  }

  hasSize(): boolean {
    return this.points.length > 0
  }
}
